import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Upload, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { API_BASE } from '@/utils/api';
import { FloorPlan } from './FloorPlan';
import { DECOR_PALETTE, TEXTURES, FORMAS, isDecor } from '../shared';

const API = API_BASE;
const TYPE_BY_FORMA = { sombrilla: 'umbrella', carpa: 'tent' };

const Section = ({ title, children }) => (
  <section className="space-y-2"><h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{title}</h3>{children}</section>
);

const UnitForm = ({ unit, onSave, onDelete }) => {
  const [f, setF] = useState(unit);
  useEffect(() => setF(unit), [unit]);
  if (isDecor(unit)) {
    return (
      <div className="space-y-2" data-testid="decor-form">
        <Input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} aria-label="Nombre" data-testid="decor-name-input" />
        <div className="flex gap-2">
          <Button className="flex-1" onClick={() => onSave({ name: f.name })} data-testid="decor-save-btn">Guardar</Button>
          <Button variant="outline" onClick={onDelete} aria-label="Quitar" data-testid="decor-delete-btn"><Trash2 className="w-4 h-4" /></Button>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="unit-form">
      <Input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} aria-label="Nombre" data-testid="unit-name-input" />
      <div className="grid grid-cols-3 gap-1">
        {FORMAS.map(([k, l]) => (
          <button key={k} onClick={() => setF({ ...f, forma: k })} data-testid={`forma-${k}`}
            className={`h-9 rounded-md text-xs font-semibold border ${f.forma === k ? 'm-sel' : 'border-border'}`}>{l}</button>
        ))}
      </div>
      <label className="flex items-center gap-2 text-sm">Capacidad
        <Input type="number" min={1} max={20} className="w-20" value={f.capacity} onChange={e => setF({ ...f, capacity: Number(e.target.value) })} data-testid="unit-capacity-input" />
      </label>
      <div className="flex gap-2">
        <Button className="flex-1" onClick={() => onSave({ name: f.name, forma: f.forma, capacity: f.capacity })} data-testid="unit-save-btn">Guardar</Button>
        <Button variant="outline" onClick={onDelete} aria-label="Eliminar" data-testid="unit-delete-btn"><Trash2 className="w-4 h-4" /></Button>
      </div>
    </div>
  );
};

export default function ModernFloorEditor() {
  const navigate = useNavigate();
  const { spaceId } = useParams();
  const [spaces, setSpaces] = useState([]);
  const [current, setCurrent] = useState(null);
  const [units, setUnits] = useState([]);
  const [picked, setPicked] = useState(null);

  const loadUnits = useCallback(async (sid) => {
    const { data } = await axios.get(`${API}/spaces/${sid}/units`);
    setUnits(data.map(u => ({ ...u, state: 'free' })));
  }, []);
  useEffect(() => {
    axios.get(`${API}/spaces`).then(({ data }) => {
      setSpaces(data);
      setCurrent(data.find(s => s.space_id === spaceId) || data[0] || null);
    });
  }, [spaceId]);
  useEffect(() => { if (current) loadUnits(current.space_id); }, [current, loadUnits]);

  const update = async (id, body) => {
    try { await axios.put(`${API}/units/${id}`, body); await loadUnits(current.space_id); } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo guardar'); }
  };
  const create = async (body) => {
    try { await axios.post(`${API}/spaces/${current.space_id}/units`, { position_x: 50, position_y: 50, ...body }); await loadUnits(current.space_id); }
    catch (e) { toast.error(e.response?.data?.detail || 'No se pudo agregar'); }
  };
  const remove = async (id) => { await axios.delete(`${API}/units/${id}`); setPicked(null); await loadUnits(current.space_id); };
  const fondo = units.find(u => u.unit_type === 'fondo');
  const setTexture = async (t) => {
    if (fondo) await update(fondo.unit_id, { name: t }); else await create({ name: t, unit_type: 'fondo', capacity: 0 });
  };
  const upload = async (file) => {
    const fd = new FormData(); fd.append('file', file); fd.append('purpose', 'floor_plan');
    try {
      const { data } = await axios.post(`${API}/upload/image`, fd);
      await axios.put(`${API}/spaces/${current.space_id}`, { map_image_url: data.url });
      if (fondo) await axios.delete(`${API}/units/${fondo.unit_id}`);
      const { data: sp } = await axios.get(`${API}/spaces`); setSpaces(sp); setCurrent(sp.find(s => s.space_id === current.space_id));
      toast.success('Fondo actualizado');
    } catch { toast.error('No se pudo subir la imagen'); }
  };
  const service = units.filter(u => !isDecor(u)).length;
  const pickedUnit = units.find(u => u.unit_id === picked);
  if (!current) return <div className="p-8 text-muted-foreground">Creá un espacio desde el editor clásico para empezar.</div>;
  return (
    <div className="p-4 md:p-6 flex flex-col lg:flex-row gap-4" data-testid="modern-floor-editor">
      <aside className="m-card p-4 w-full lg:w-72 shrink-0 space-y-5">
        <Button variant="ghost" size="sm" onClick={() => navigate('/pos')} data-testid="editor-back-btn"><ArrowLeft className="w-4 h-4 mr-1" />Volver al plano</Button>
        <Section title="Espacio">
          <div className="flex flex-wrap gap-1">
            {spaces.map(s => (
              <button key={s.space_id} onClick={() => { setCurrent(s); setPicked(null); }} data-testid={`editor-space-${s.space_id}`}
                className={`h-8 px-3 rounded-md text-sm font-semibold ${current.space_id === s.space_id ? 'bg-[#111827] text-white' : 'bg-muted'}`}>{s.name}</button>
            ))}
          </div>
        </Section>
        <Section title="Fondo">
          <div className="grid grid-cols-3 gap-1.5">
            {TEXTURES.map(([k, l]) => (
              <button key={k} onClick={() => setTexture(k)} data-testid={`texture-${k}`} aria-pressed={fondo?.name === k}
                className={`relative h-14 rounded-md overflow-hidden text-[11px] font-bold mtex-${k} ${fondo?.name === k ? 'ring-2 ring-[#2563EB]' : ''}`}>
                <span className="absolute bottom-0 inset-x-0 bg-black/70 text-white py-0.5">{l}</span>
              </button>
            ))}
          </div>
          <label className="flex items-center justify-center gap-2 h-9 rounded-md border border-dashed border-border text-sm cursor-pointer hover:bg-muted" data-testid="bg-upload">
            <Upload className="w-4 h-4" />Subir imagen
            <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && upload(e.target.files[0])} />
          </label>
        </Section>
        <Section title="Agregar">
          <div className="grid grid-cols-2 gap-1.5">
            {FORMAS.map(([k, l]) => (
              <Button key={k} variant="outline" size="sm" onClick={() => create({ name: `${k === 'sombrilla' ? 'Sombrilla' : k === 'carpa' ? 'Carpa' : 'Mesa'} ${service + 1}`, unit_type: TYPE_BY_FORMA[k] || 'table', forma: k, capacity: k === 'rectangular' ? 6 : k === 'carpa' ? 6 : k === 'sombrilla' ? 2 : 4 })}
                data-testid={`add-unit-${k}`}><Plus className="w-3 h-3 mr-1" />{l}</Button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-1.5 pt-1">
            {DECOR_PALETTE.map(([k, l]) => (
              <Button key={k} variant="secondary" size="sm" onClick={() => create({ name: l, unit_type: k, capacity: 0 })} data-testid={`add-decor-${k}`}>{l}</Button>
            ))}
          </div>
        </Section>
        {pickedUnit && <Section title="Seleccionado"><UnitForm unit={pickedUnit} onSave={(b) => update(pickedUnit.unit_id, b)} onDelete={() => remove(pickedUnit.unit_id)} /></Section>}
        <p className="text-xs text-muted-foreground">Arrastrá mesas y decorados para ubicarlos. Tocá uno para editarlo.</p>
      </aside>
      <div className="flex-1 min-w-0">
        <FloorPlan units={units} spaces={spaces} spaceId={current.space_id} view="real" now={Date.now()} editable
          selectedId={picked} onSelect={(u) => setPicked(u.unit_id)} onPickDecor={(d) => setPicked(d.unit_id)} pickedDecor={picked}
          onMove={(id, x, y) => update(id, { position_x: x, position_y: y })} />
      </div>
    </div>
  );
}
